import {combineLatest as observableCombineLatest, Observable, from as fromPromise, of, ReplaySubject,BehaviorSubject} from 'rxjs';
import { Injectable } from '@angular/core';

import { catchError ,  tap, switchMap, map } from 'rxjs/operators';

import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AuthService } from '../../account/shared/auth.service';
import { FileUploadService } from './file-upload.service';
import { MessageService } from '../../messages/message.service';
import { ProductRatingService } from './product-rating.service';

import { Product } from '../../models/product.model';
import { ProductsUrl } from './productsUrl';
import {addDoc} from '@angular/fire/firestore';



// Interface pour les données de filtres optimisées
interface ProductFilter {
  id: number;
  name: string;
  price: number;
  priceNormal: number;
  reduction: number;
  category: string;
  famille: string;
  sousFamille: string;
  marque: string;
  sale: boolean;
  currentRating: number;
  date: string;
}

// Interface pour les options de filtres
interface FilterOptions {
  categories: CategoryData[];
}

interface PriceRange {
  min: number;
  max: number;
  count: number;
}

interface SousFamilleData {
  sousFamille: string;
  marques: string[];
  priceRanges: PriceRange[];
}

interface FamilleData {
  famille: string;
  sousFamilles: SousFamilleData[];
  marques: string[];
  priceRanges: PriceRange[];
}

interface CategoryData {
  category: string;
  familles: FamilleData[];
  marques: string[];
  priceRanges: PriceRange[];
}

@Injectable()
export class ProductService {
  private productsUrl = ProductsUrl.productsUrl;
  cachedproducts: Product[];
  lastCategory ;



  constructor(
    private messageService: MessageService,
    private angularFireDatabase: AngularFireDatabase,
    public authService: AuthService,
    private uploadService: FileUploadService,
    private productRatingService: ProductRatingService
  ) {}

  /** Log a ProductService message with the MessageService */
  private log(message: string) {
    this.messageService.add('ProductService: ' + message);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error); // log to console instead
      this.log(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  // NOUVELLES MÉTHODES OPTIMISÉES POUR LA NAVIGATION ET FILTRES

  /**
   * Obtenir les données légères pour navigation et filtres
   */
  public getProductFilters(): Observable<ProductFilter[]> {
    return this.angularFireDatabase
      .list<ProductFilter>('product-filters', (ref) => ref.orderByChild('date'))
      .valueChanges()
      .pipe(
        map((arr) => arr.reverse()),
        catchError(this.handleError<ProductFilter[]>(`getProductFilters`))
      );
  }

  /**
   * Rechercher les produits avec filtres multiples (version optimisée)
   */
  public searchProductsOptimized(filters: {
    category?: string;
    famille?: string;
    sousFamille?: string;
    marque?: string;
    priceMin?: number;
    priceMax?: number;
    onSale?: boolean;
    searchTerm?: string;
    limitToFirst?: number;
  }): Observable<ProductFilter[]> {
    return this.angularFireDatabase
      .list<ProductFilter>('product-filters')
      .valueChanges()
      .pipe(
        map(products => {
          let filtered = products;

          // Appliquer les filtres
          if (filters.category) {
            console.log('filtre par la category :'+ filters.category);
            filtered = filtered.filter(p => p.category === filters.category);
          }
          if (filters.famille) {
            filtered = filtered.filter(p => p.famille === filters.famille);
          }
          if (filters.sousFamille) {
            filtered = filtered.filter(p => p.sousFamille === filters.sousFamille);
          }
          if (filters.marque) {
            filtered = filtered.filter(p => p.marque === filters.marque);
          }
          if (filters.priceMin !== undefined) {
            filtered = filtered.filter(p => p.price >= filters.priceMin!);
          }
          if (filters.priceMax !== undefined) {
            filtered = filtered.filter(p => p.price <= filters.priceMax!);
          }
          if (filters.onSale) {
            filtered = filtered.filter(p => p.sale || p.reduction > 0);
          }
          if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(p =>
              p.name.toLowerCase().includes(term) ||
              p.marque.toLowerCase().includes(term)
            );
          }

          // Tri par date (plus récent en premier)
          filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

          // Limitation
          if (filters.limitToFirst) {
            filtered = filtered.slice(0, filters.limitToFirst);
          }
          return filtered;
        }),
        catchError(this.handleError<ProductFilter[]>(`searchProductsOptimized`))
      );
  }

  /**
   * Obtenir les options de filtres disponibles
   */
/*
  public getFilterOptions(): Observable<FilterOptions> {
    return this.angularFireDatabase
      .list<ProductFilter>('product-filters')
      .valueChanges()
      .pipe(
        map(products => {
          const categories = new Set<string>();
          const familles = new Set<string>();
          const sousFamilles = new Set<string>();
          const marques = new Set<string>();
          const prices: number[] = [];

          products.forEach(product => {
            if (product.category) categories.add(product.category);
            if (product.famille) familles.add(product.famille);
            if (product.sousFamille) sousFamilles.add(product.sousFamille);
            if (product.marque) marques.add(product.marque);
            prices.push(product.price);
          });

          // Calculer les gammes de prix
          const priceRanges = [
            { min: 0, max: 50, count: prices.filter(p => p < 50).length },
            { min: 50, max: 100, count: prices.filter(p => p >= 50 && p < 100).length },
            { min: 100, max: 200, count: prices.filter(p => p >= 100 && p < 200).length },
            { min: 200, max: 500, count: prices.filter(p => p >= 200 && p < 500).length },
            { min: 500, max: Infinity, count: prices.filter(p => p >= 500).length }
          ];

          return {
            categories: Array.from(categories).sort(),
            familles: Array.from(familles).sort(),
            sousFamilles: Array.from(sousFamilles).sort(),
            marques: Array.from(marques).sort(),
            priceRanges: priceRanges.filter(range => range.count > 0)
          };
        }),
        catchError(this.handleError<FilterOptions>('getFilterOptions', {
          categories: [],
          familles: [],
          sousFamilles: [],
          marques: [],
          priceRanges: []
        }))
      );
  }
*/

  /**
   * Recherche optimisée par index de catégorie
   */
  public getProductsByCategoryOptimized(category: string, limitToFirst: number = 36): Observable<ProductFilter[]> {
    return this.angularFireDatabase
      .list<ProductFilter>(`indexes/by-category/${category}`, (ref) =>
        ref.limitToFirst(limitToFirst)
      )
      .valueChanges()
      .pipe(
        catchError(this.handleError<ProductFilter[]>(`getProductsByCategoryOptimized`))
      );
  }

  /**
   * Obtenir les promotions depuis l'index optimisé
   */
  public getPromotionsOptimized(limitToFirst: number = 20): Observable<ProductFilter[]> {
    return this.angularFireDatabase
      .list<ProductFilter>('indexes/promotions', (ref) =>
        ref.limitToFirst(limitToFirst)
      )
      .valueChanges()
      .pipe(
        catchError(this.handleError<ProductFilter[]>(`getPromotionsOptimized`))
      );
  }

  // MÉTHODES EXISTANTES MODIFIÉES

  public getProducts(): Observable<Product[]> {
    return this.angularFireDatabase
      .list<Product>('products', (ref) => ref.orderByChild('date'))
      .valueChanges()
      .pipe(map((arr) => arr.reverse()), catchError(this.handleError<Product[]>(`getProducts`)));
  }

  public getProductsQuery(
    byChild: string,
    equalTo: string | boolean,
    limitToFirst: number
  ): Observable<Product[]> {
    return this.angularFireDatabase
      .list<Product>('products', (ref) =>
        ref
          .orderByChild(byChild)
          .equalTo(equalTo)
          .limitToFirst(limitToFirst)
      )
      .valueChanges()
      .pipe(catchError(this.handleError<Product[]>(`getProductsQuery`)));
  }

  public findProducts(term: string): Observable<Product[]> {
    const lowerCaseTerm = term.toLowerCase();
    return this.angularFireDatabase
      .list<Product>('products')
      .valueChanges()
      .pipe(
        map((products) =>
          products.filter(
            (product) =>
              product.name.toLowerCase().includes(lowerCaseTerm) ||
              product.marque?.toLowerCase().includes(lowerCaseTerm)
          )
        ),
        catchError(this.handleError<Product[]>(`findProducts term=${term}`))
      );
  }

  public getProductsByDate(limitToLast: number): Observable<Product[]> {
    return this.angularFireDatabase
      .list<Product>('products', (ref) =>
        ref.orderByChild('date').limitToLast(limitToLast)
      )
      .valueChanges()
      .pipe(
        map((arr) => arr.reverse()),
        catchError(this.handleError<Product[]>(`getProductsByDate`))
      );
  }

  public getProductsByRating(limitToLast: number ): Observable<Product[]> {
    return this.angularFireDatabase
      .list<Product>('products', (ref) =>
        ref.orderByChild('currentRating').limitToLast(limitToLast)
      )
      .valueChanges()
      .pipe(map((arr) => arr.reverse()), catchError(this.handleError<Product[]>(`getProductsByRating`)));
  }

  public getProductsBySousFamille(sousFamille: string ,limitToLast ?: number ): Observable<Product[]> {
    return this.angularFireDatabase
      .list<Product>('products', (ref) =>
        ref.orderByChild(`sousFamilles/${sousFamille}`).equalTo(true)
          .limitToLast(limitToLast)
      )
      .valueChanges()
      .pipe(
        map((arr) => arr.reverse()),
        catchError(this.handleError<Product[]>(`getProductsBySousFamille`))
      );
  }

  public deleteProduct2(qalwa: string) {
    const url = `${this.productsUrl}/${qalwa}`;

    // this.uploadService.deleteFile(product.imageRefs);

    return this.angularFireDatabase
      .object<Product>(url)
      .remove()
      .then(() => this.log('success deleting' + qalwa))
      .catch((error) => {
        this.messageService.addError('Delete failed ' + qalwa);
        this.handleError('delete product');
      });
  }

  public getProductsByFamille(famille: string ,limitToLast ?: number ): Observable<Product[]> {
    return this.angularFireDatabase
      .list<Product>('products', (ref) =>
        ref.orderByChild(`familles/${famille}_xlsx`).equalTo(true)
          .limitToLast(limitToLast)
      )
      .valueChanges()
      .pipe(
        map((arr) => arr.reverse()),
        catchError(this.handleError<Product[]>(`getProductsByFamille`))
      );
  }

  public getFeaturedProducts(): Observable<any[]> {
    return this.angularFireDatabase
      .list<Product>('featured')
      .snapshotChanges()
      .pipe(
        switchMap(
          (actions) => {
            return observableCombineLatest(
              actions.map((action) => this.getProduct(action.key))
            );
          },
          (actionsFromSource, resolvedProducts) => {
            resolvedProducts.map((product, i) => {
              product['imageFeaturedUrl'] = actionsFromSource[
                i
                ].payload.val().imageFeaturedUrl;
              return product;
            });
            return resolvedProducts;
          }
        ),
        catchError(this.handleError<Product[]>(`getFeaturedProducts`)));
  }
  public getProductBySku(name: string): Observable<Product | null> {
    return this.angularFireDatabase
      .list<Product>(this.productsUrl, ref => ref.orderByChild('name').equalTo(name))
      .snapshotChanges()
      .pipe(
        map(changes => {
          const data = changes.map(c => ({
            id: c.payload.key, // Récupère l'ID du produit
            ...c.payload.val() // Récupère les données du produit
          } as Product));
          return data.length > 0 ? data[0] : null; // Retourne le premier produit ou null
        }),
        tap((result) => {
          if (result) {
            this.messageService.add(`Found product with Name=${name}`);
          } else {
            this.messageService.addError(`Found no product with Name=${name}`);
          }
        }),
        catchError(this.handleError<Product>(`getProductBySku name=${name}`))
      );
  }

  public getProduct(id: any): Observable<Product | null> {
    const url = `${this.productsUrl}/${id}`;
    return this.angularFireDatabase
      .object<Product>(url)
      .valueChanges()
      .pipe(
        tap((result) => {
          if (result) {
            return of(result);
          } else {
            this.messageService.addError(`Found no Product with id=${id}`);
            return of(null);
          }
        }),
        catchError(this.handleError<Product>(`getProduct id=${id}`))
      );
  }

  ///// put et post

  public updateProduct(data: { product: Product; files: FileList }) {
    const url = `${this.productsUrl}/${data.product.id}`;

    if (!data.files.length) {
      return this.updateProductWithoutNewImage(data.product, url);
    }

    const dbOperation = this.uploadService
      .startUpload(data)
      .then(async (task) => {
        const downloadUrl = await task.ref.getDownloadURL()
        data.product.imageURLs[0] = downloadUrl;
        data.product.imageRefs[0] = task.ref.fullPath;

        return data;
      })
      .then((dataWithImagePath) => {
        return this.angularFireDatabase
          .object<Product>(url)
          .update(data.product);
      })
      .then(async (response) => {
        this.log(`Updated Product ${data.product.name}`);
        // Mettre à jour les données de filtres optimisées
        await this.updateProductFilter(data.product);
        return data.product;
      })
      .catch((error) => {
        this.handleError(error);
        return error;
      });
    return fromPromise(dbOperation);
  }

  private updateProductWithoutNewImage(product: Product, url: string) {
    const dbOperation = this.angularFireDatabase
      .object<Product>(url)
      .update(product)
      .then(async (response) => {
        this.log(`Updated Product ${product.name}`);
        // Mettre à jour les données de filtres optimisées
        await this.updateProductFilter(product);
        return product;
      })
      .catch((error) => {
        this.handleError(error);
        return error;
      });
    return fromPromise(dbOperation);
  }

  public addProduct(data: { product: Product; files: FileList }) {
    const dbOperation = this.uploadService
      .startUpload(data)
      .then(async (task) => {
        const downloadUrl = await task.ref.getDownloadURL()
        data.product.imageURLs.push(downloadUrl);
        data.product.imageRefs.push(task.ref.fullPath);

        return this.angularFireDatabase
          .list('products')
          .set(data.product.id.toString(), data.product);
      }, (error) => error)
      .then(async (response) => {
        this.log(`Added Product ${data.product.name}`);
        // Créer les données de filtres optimisées
        await this.createProductFilter(data.product);
        return data.product;
      })
      .catch((error) => {
        this.messageService.addError(
          `Add Failed, Product ${data.product.name}`
        );
        this.handleError(error);
        return error;
      });
    return fromPromise(dbOperation);
  }

  public deleteProduct(product: Product) {
    const url = `${this.productsUrl}/${product.id}`;

    this.uploadService.deleteFile(product.imageRefs);

    return this.angularFireDatabase
      .object<Product>(url)
      .remove()
      .then(async () => {
        this.log('success deleting' + product.name);
        // Supprimer des données de filtres
        await this.deleteProductFilter(product.id);
      })
      .catch((error) => {
        this.messageService.addError('Delete failed ' + product.name);
        this.handleError('delete product');
      });
  }

  async addProducts(products: Product[]) {
    try {
      for (const product of products) {
        const path = `products/${product.id}`;
        await this.angularFireDatabase.object(path).set({ ...product });
        // Créer les données de filtres optimisées
        await this.createProductFilter(product);
      }
      console.log('Produits ajoutés à la Realtime Database');
    } catch (error) {
      console.error('Erreur lors de l\'ajout des produits :', error);
      throw error;
    }
  }

  // MÉTHODES UTILITAIRES POUR LA STRUCTURE OPTIMISÉE

  /**
   * Créer les données de filtres optimisées
   */
  private async createProductFilter(product: Product): Promise<void> {
    const filterData: ProductFilter = {
      id: product.id,
      name: product.name,
      price: product.price,
      priceNormal: product.priceNormal,
      reduction: product.reduction,
      category: this.extractMainValue(product.categories),
      famille: this.extractMainValue(product.familles),
      sousFamille: this.extractMainValue(product.sousFamilles),
      marque: product.marque,
      sale: product.sale,
      currentRating: product.currentRating,
      date: product.date
    };

    // Sauvegarder les données de filtres
    await this.angularFireDatabase.object(`product-filters/${product.id}`).set(filterData);

    // Mettre à jour les index
    await this.updateSearchIndexes(filterData);
  }

  /**
   * Mettre à jour les données de filtres optimisées
   */
  private async updateProductFilter(product: Product): Promise<void> {
    await this.createProductFilter(product); // Même logique que création
  }

  /**
   * Supprimer les données de filtres optimisées
   */
  private async deleteProductFilter(productId: number): Promise<void> {
    // Supprimer des filtres
    await this.angularFireDatabase.object(`product-filters/${productId}`).remove();

    // Supprimer des index (nécessite de connaître les anciennes valeurs)
    // Pour simplifier, on peut reconstruire les index périodiquement
  }

  /**
   * Extraire la valeur principale d'un objet (première clé)
   */
  private extractMainValue(obj: any): string {
    if (typeof obj === 'object' && obj !== null) {
      const keys = Object.keys(obj);
      return keys.length > 0 ? keys[0].replace('_xlsx', '') : '';
    }
    return obj?.toString() || '';
  }

  /**
   * Mettre à jour les index de recherche
   */
  private async updateSearchIndexes(filterData: ProductFilter): Promise<void> {
    const updates: any = {};

    // Nettoyage des clés
    const categoryKey = filterData.category ? this.sanitizeKey(filterData.category) : null;
    const familleKey = filterData.famille ? this.sanitizeKey(filterData.famille) : null;
    const marqueKey = filterData.marque ? this.sanitizeKey(filterData.marque) : null;

    // Index par catégorie
    if (categoryKey) {
      updates[`indexes/by-category/${categoryKey}/${filterData.id}`] = {
        name: filterData.name,
        price: filterData.price,
        reduction: filterData.reduction,
        marque: filterData.marque, // on garde l'original ici
      };
    }

    // Index par famille
    if (familleKey) {
      updates[`indexes/by-famille/${familleKey}/${filterData.id}`] = {
        name: filterData.name,
        price: filterData.price,
        reduction: filterData.reduction,
        marque: filterData.marque,
      };
    }

    // Index par marque
    if (marqueKey) {
      updates[`indexes/by-marque/${marqueKey}/${filterData.id}`] = {
        name: filterData.name,
        price: filterData.price,
        reduction: filterData.reduction,
        category: filterData.category,
      };
    }

    // Index par promotion
    if (filterData.sale || filterData.reduction > 0) {
      updates[`indexes/promotions/${filterData.id}`] = {
        name: filterData.name,
        price: filterData.price,
        priceNormal: filterData.priceNormal,
        reduction: filterData.reduction,
        marque: filterData.marque,
        category: filterData.category,
      };
    }

    if (Object.keys(updates).length > 0) {
      await this.angularFireDatabase.object('/').update(updates);
    }
  }

  // MÉTHODES EXISTANTES CONSERVÉES

  private familiesCache = new ReplaySubject<string[]>(1);
  public getFamilies(): Observable<string[]> {
    return this.angularFireDatabase
      .list<any>('products')
      .valueChanges()
      .pipe(
        map(products => {
          if (!products || !Array.isArray(products)) {
            return [];
          }
          // Extraire toutes les clés de 'familles' à travers tous les produits
          const allFamilies = products.reduce((acc, product) => {
            const families = product.categories || {};
            return { ...acc, ...families };
          }, {});
          // Retourner les clés uniques et triées
          return Object.keys(allFamilies).sort();
        }),
        catchError(this.handleError<string[]>('getFamilies', []))
      );
  }

  getProductsByCategorie(category: any) {
    return this.angularFireDatabase
      .list<Product>('products', (ref) =>
        ref.orderByChild(`categories/${category}`).equalTo(true)
        //  .limitToLast(36)
      )
      .valueChanges()
      .pipe(
        map((arr) => arr.reverse()),
        catchError(this.handleError<Product[]>(`getProductsByFamille`))
      );
  }








  public getFilterOptions2(): Observable<FilterOptions> {
    console.log('dkhal')
    return this.angularFireDatabase
      .list<ProductFilter>('product-filters')
      .valueChanges()
      .pipe(
        map(products => {
          // Structure pour stocker les données intermédiaires
          const categoryMap = new Map<string, {
            familles: Map<string, {
              sousFamilles: Set<string>;
              marques: Set<string>;
              prices: number[];
            }>;
            marques: Set<string>;
            prices: number[];
          }>();

          // Parcourir les produits pour collecter les données
          products.forEach(product => {
            if (!product.category) return;

            // Initialiser la catégorie si elle n'existe pas
            if (!categoryMap.has(product.category)) {
              categoryMap.set(product.category, {
                familles: new Map(),
                marques: new Set(),
                prices: []
              });
            }

            const categoryData = categoryMap.get(product.category)!;

            // Ajouter la marque à la catégorie
            if (product.marque) {
              categoryData.marques.add(product.marque);
            }

            // Ajouter le prix à la catégorie
            categoryData.prices.push(product.price);

            // Gérer les familles et sous-familles
            if (product.famille) {
              if (!categoryData.familles.has(product.famille)) {
                categoryData.familles.set(product.famille, {
                  sousFamilles: new Set(),
                  marques: new Set(),
                  prices: []
                });
              }

              const familleData = categoryData.familles.get(product.famille)!;

              // Ajouter la sous-famille
              if (product.sousFamille) {
                familleData.sousFamilles.add(product.sousFamille);
              }

              // Ajouter la marque à la famille
              if (product.marque) {
                familleData.marques.add(product.marque);
              }

              // Ajouter le prix à la famille
              familleData.prices.push(product.price);
            }
          });

          // Fonction pour calculer les gammes de prix
          const calculatePriceRanges = (prices: number[]): PriceRange[] => {
            const ranges = [
              { min: 0, max: 50, count: prices.filter(p => p < 50).length },
              { min: 50, max: 100, count: prices.filter(p => p >= 50 && p < 100).length },
              { min: 100, max: 200, count: prices.filter(p => p >= 100 && p < 200).length },
              { min: 200, max: 500, count: prices.filter(p => p >= 200 && p < 500).length },
              { min: 500, max: Infinity, count: prices.filter(p => p >= 500).length }
            ];
            return ranges.filter(range => range.count > 0);
          };

          // Construire la structure finale
          const categories: CategoryData[] = Array.from(categoryMap.entries()).map(([category, data]) => {
            const familles: FamilleData[] = Array.from(data.familles.entries()).map(([famille, familleData]) => ({
              famille,
              sousFamilles: Array.from(familleData.sousFamilles).sort().map(sousFamille => ({
                sousFamille,
                marques: Array.from(familleData.marques).sort(),
                priceRanges: calculatePriceRanges(familleData.prices)
              })),
              marques: Array.from(familleData.marques).sort(),
              priceRanges: calculatePriceRanges(familleData.prices)
            }));

            return {
              category,
              familles: familles.sort((a, b) => a.famille.localeCompare(b.famille)),
              marques: Array.from(data.marques).sort(),
              priceRanges: calculatePriceRanges(data.prices)
            };
          });

          return {
            categories: categories.sort((a, b) => a.category.localeCompare(b.category))
          };
        }),
        catchError(this.handleError<FilterOptions>('getFilterOptions2', { categories: [] }))
      );
  }



  private sanitizeKey(key: any): string {
    if (typeof key !== 'string') {
      key = String(key ?? '');
    }
    return key.replace(/[.#$/[\]]/g, '_');
  }


  private selectedProduct = new BehaviorSubject<Product | null>(null);


  setProduct(product: Product): void {
    this.selectedProduct.next(product);
  }

  getStateProduct() {
    return this.selectedProduct.asObservable();
  }



}
